import { db } from '../config/firebase';
import { collection, addDoc, getDocs, doc, getDoc, serverTimestamp, orderBy, query } from 'firebase/firestore';

const CAMPAIGN_EPISODES_COLLECTION = 'campaignEpisodes';

/**
 * Creates a new campaign episode entry in Firestore.
 * @param {object} episodeData - The data for the new episode.
 * @param {string} episodeData.title - The title of the episode.
 * @param {string} [episodeData.youtubeVideoUrl] - Optional URL of the YouTube video for the episode.
 * @param {string} [episodeData.description] - Optional description for the episode.
 * @param {number} [episodeData.episodeNumber] - Optional episode number for ordering.
 * @param {string} [episodeData.relatedBennyJournalId] - Optional ID of the related Benny's Journal entry.
 * @returns {Promise<string>} The ID of the newly created campaign episode.
 */
export const createCampaignEpisode = async (episodeData) => {
  if (!episodeData.title) { // userId, userName and youtubeVideoUrl removed from this check
    throw new Error('Title is required to create an episode.');
  }
  try {
    const docRef = await addDoc(collection(db, CAMPAIGN_EPISODES_COLLECTION), {
      ...episodeData,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating campaign episode: ", error);
    throw error;
  }
};

/**
 * Fetches all campaign episodes from Firestore, ordered by creation date (newest first).
 * You might want to order by episodeNumber if that's consistently used.
 * @returns {Promise<Array<object>>} An array of campaign episode objects, each with its ID.
 */
export const getAllCampaignEpisodes = async () => {
  try {
    const q = query(collection(db, CAMPAIGN_EPISODES_COLLECTION), orderBy('createdAt', 'desc'));
    // If you prefer ordering by episode number (and it's reliably present):
    // const q = query(collection(db, CAMPAIGN_EPISODES_COLLECTION), orderBy('episodeNumber', 'asc'));
    const querySnapshot = await getDocs(q);
    const episodes = [];
    querySnapshot.forEach((doc) => {
      episodes.push({ id: doc.id, ...doc.data() });
    });
    return episodes;
  } catch (error) {
    console.error("Error fetching all campaign episodes: ", error);
    throw error;
  }
};

/**
 * Fetches a single campaign episode by its ID from Firestore.
 * @param {string} episodeId - The ID of the episode to fetch.
 * @returns {Promise<object|null>} The episode object if found, otherwise null.
 */
export const getCampaignEpisodeById = async (episodeId) => {
  if (!episodeId) {
    throw new Error('Episode ID is required to fetch an episode.');
  }
  try {
    const episodeRef = doc(db, CAMPAIGN_EPISODES_COLLECTION, episodeId);
    const docSnap = await getDoc(episodeRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    console.warn(`Campaign episode with ID ${episodeId} not found.`);
    return null;
  } catch (error) {
    console.error(`Error fetching campaign episode by ID ${episodeId}:`, error);
    throw error;
  }
};
